import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import axios from "axios";
import "./style.css";

const isBirthdayToday = (birthday) => {
  const today = new Date();
  return (
    Number(birthday.birthMonth) === today.getMonth() + 1 &&
    Number(birthday.birthDay) === today.getDate()
  );
};

export default function Birthdays() {
  const { user } = useSelector((state) => ({ ...state }));
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [wishingUserId, setWishingUserId] = useState(null);
  const [wishedUsers, setWishedUsers] = useState(new Set());

  const getBirthdays = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/birthdays/upcoming", {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      const birthdays = response.data.birthdays;
      setBirthdays(birthdays);
      setError("");

      // Check wish status only for birthdays that are today
      if (birthdays && user.token) {
        const todayBirthdays = birthdays.filter((b) => isBirthdayToday(b));
        const wishStatusPromises = todayBirthdays.map(async (birthday) => {
          try {
            const statusResponse = await axios.get(
              `/api/messenger/messages/birthday-wish/status?participantId=${birthday._id}`,
              {
                headers: {
                  Authorization: `Bearer ${user.token}`,
                },
              }
            );
            return { userId: birthday._id, hasWished: statusResponse.data.hasWished };
          } catch (error) {
            console.error(`Error checking wish status for ${birthday._id}:`, error);
            return { userId: birthday._id, hasWished: false };
          }
        });

        const wishStatuses = await Promise.all(wishStatusPromises);
        const wishedSet = new Set(
          wishStatuses.filter((status) => status.hasWished).map((status) => status.userId)
        );
        setWishedUsers(wishedSet);
      }
    } catch (error) {
      console.error("Error fetching birthdays:", error);
      setError("Failed to load birthdays");
    } finally {
      setLoading(false);
    }
  }, [user.token]);

  useEffect(() => {
    getBirthdays();
  }, [getBirthdays]);

  const formatBirthdayMessage = (birthday) => {
    if (isBirthdayToday(birthday)) {
      return `🎉 Today! Turning ${birthday.age}`;
    } else if (birthday.daysUntil === 1) {
      return `🎂 Tomorrow! Turning ${birthday.age + 1}`;
    } else if (birthday.daysUntil <= 7) {
      return `🗓️ In ${birthday.daysUntil} days - Turning ${birthday.age + 1}`;
    } else {
      return `📅 In ${birthday.daysUntil} days - Turning ${birthday.age + 1}`;
    }
  };

  const getMonthName = (month) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months[month - 1];
  };

  const handleWishBirthday = async (e, birthday) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user || !user.token) {
      alert("Please log in to send birthday wishes");
      return;
    }

    if (!isBirthdayToday(birthday)) {
      alert("Birthday wishes can only be sent on the user's birthday.");
      return;
    }

    const birthdayUserId = birthday._id;
    setWishingUserId(birthdayUserId);

    try {
      // Send birthday wish message and create notification
      const birthdayMessage = `🎂🎉 Happy Birthday ${birthday.name}! Wishing you a wonderful day filled with joy and happiness! 🎈🎁`;

      await axios.post(
        "/api/messenger/messages/birthday-wish",
        {
          participantId: birthdayUserId,
          message: birthdayMessage
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Mark user as wished
      setWishedUsers((prev) => new Set([...prev, birthdayUserId]));

      // Show success message
      alert(`Birthday wish sent to ${birthday.name}! 🎉`);
    } catch (error) {
      console.error("Error sending birthday wish:", error);
      const errorMessage = error.message || error.response?.data?.message || "Failed to send birthday wish";
      alert(`Error: ${errorMessage}`);
    } finally {
      setWishingUserId(null);
    }
  };

  if (loading) {
    return (
      <div className="birthdays_container">
        <div className="friends_left_header">
          <h3>Upcoming Birthdays</h3>
        </div>
        <div className="loading">Loading birthdays...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="birthdays_container">
        <div className="friends_left_header">
          <h3>Upcoming Birthdays</h3>
        </div>
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="birthdays_container">
      <div className="friends_left_header">
        <h3>Upcoming Birthdays</h3>
        <div className="birthdays_count">
          {birthdays.length > 0 && `${birthdays.length} upcoming`}
        </div>
      </div>

      {birthdays.length === 0 ? (
        <div className="no_data">
          <div className="birthday_icon">🎂</div>
          <p>No upcoming birthdays in the next 30 days</p>
          <p className="sub_text">Check back later!</p>
        </div>
      ) : (
        <div className="birthdays_list">
          {birthdays.map((birthday) => {
            const birthdayToday = isBirthdayToday(birthday);

            return (
              <div key={birthday._id} className={`birthday_card ${birthdayToday ? "today" : ""}`}>
                <Link to={`/profile/${birthday.username || birthday._id}`} className="birthday_link">
                  <div className="birthday_avatar">
                    <img
                      src={birthday.picture}
                      alt={birthday.name}
                      onError={(e) => {
                        e.target.src = "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
                      }}
                    />
                    {birthdayToday && <div className="birthday_badge">🎉</div>}
                  </div>
                  <div className="birthday_info">
                    <div className="birthday_name">{birthday.name}</div>
                    <div className="birthday_date">
                      {getMonthName(birthday.birthMonth)} {birthday.birthDay}
                    </div>
                    <div className={`birthday_message ${birthdayToday ? "today" : ""}`}>
                      {formatBirthdayMessage(birthday)}
                    </div>
                  </div>
                </Link>
                {birthdayToday && (
                  <div className="birthday_action">
                    <button
                      className={`wish_button ${wishedUsers.has(birthday._id) ? "wished" : ""}`}
                      onClick={(e) => handleWishBirthday(e, birthday)}
                      disabled={wishingUserId === birthday._id || wishedUsers.has(birthday._id)}
                    >
                      {wishingUserId === birthday._id
                        ? "Sending..."
                        : wishedUsers.has(birthday._id)
                          ? "✓ Wished"
                          : "🎂 Wish Happy Birthday"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
